// BCG 신호 처리 유틸리티 함수들

import { BCGData, WindowData } from './types';

/**
 * CSV 데이터에서 BCG 시계열 재구성
 */
export function reconstructSeries(csvData: any[], fsGuess: number = 256): { data: BCGData; fs: number } | null {
  // console.log(`reconstructSeries 시작 - csvData 길이: ${csvData?.length}`);

  if (!csvData || csvData.length === 0) {
    console.error('CSV 데이터가 비어있음');
    return null;
  }

  // 첫 번째 행의 필드 확인
  // console.log('첫 번째 행 필드들:', Object.keys(csvData[0]));
  // console.log('deviceTime 샘플:', csvData[0]?.deviceTime);
  // console.log('createdAt 샘플:', csvData[0]?.createdAt);
  // console.log('bcgMerge 샘플 (첫 100자):', typeof csvData[0]?.bcgMerge === 'string' ? csvData[0].bcgMerge.substring(0, 100) : csvData[0]?.bcgMerge);

  const times = csvData.map(row => new Date(row.deviceTime || row.createdAt));
  // console.log(`시간 데이터 파싱 결과 - 유효한 시간: ${times.filter(t => !isNaN(t.getTime())).length}/${times.length}`);

  const chunks = csvData.map((row, index) => {
    try {
      let bcgMergeStr = row.bcgMerge;

      if (typeof bcgMergeStr === 'string') {
        // 다양한 JSON 형식 처리
        // 1. 앞뒤 따옴표 제거
        bcgMergeStr = bcgMergeStr.replace(/^"/, '').replace(/"$/, '');
        // 2. 이중 따옴표를 단일 따옴표로 변환
        bcgMergeStr = bcgMergeStr.replace(/""/g, '"');

        if (index === 0) {
          // console.log(`첫 번째 청크 정리된 JSON:`, bcgMergeStr.substring(0, 100));
        }
      }

      const bcgMerge = typeof bcgMergeStr === 'string' ? JSON.parse(bcgMergeStr) : bcgMergeStr;
      const data = bcgMerge?.data || [];

      if (index === 0) {
        // console.log(`첫 번째 청크 - bcgMerge 타입: ${typeof bcgMerge}, data 길이: ${data.length}`);
        if (data.length > 0) {
          // console.log(`첫 번째 청크 데이터 샘플:`, data.slice(0, 5));
        }
      }
      return data;
    } catch (error) {
      if (index === 0) {
        console.error(`첫 번째 청크 JSON 파싱 오류:`, error);
        // console.log(`원본 bcgMerge:`, row.bcgMerge);
      }
      return [];
    }
  });

  const lens = chunks.map(c => c.length);
  // console.log(`청크 길이 분포:`, lens.slice(0, 10));

  let fs = fsGuess;

  // 일관된 청크 크기가 있으면 샘플링 주파수로 사용
  const uniqueLens = [...new Set(lens)];
  // console.log(`고유 청크 길이들:`, uniqueLens);

  if (uniqueLens.length === 1 && [128, 200, 256, 512].includes(uniqueLens[0])) {
    fs = uniqueLens[0];
    // console.log(`샘플링 주파수를 청크 크기로 설정: ${fs}`);
  }

  const data = chunks.flat().map(Number);
  // console.log(`전체 데이터 길이: ${data.length}, 유효한 숫자: ${data.filter(d => !isNaN(d)).length}`);

  if (times.length === 0 || data.length === 0) {
    console.error(`데이터 부족 - times: ${times.length}, data: ${data.length}`);
    return null;
  }

  // 시간 인덱스 생성
  const baseTime = times[0];
  const timeArray: Date[] = [];

  for (let i = 0; i < chunks.length; i++) {
    const chunkStartTime = new Date(baseTime.getTime() + i * 1000); // 1초 간격
    for (let j = 0; j < chunks[i].length; j++) {
      const sampleTime = new Date(chunkStartTime.getTime() + (j / fs) * 1000);
      timeArray.push(sampleTime);
    }
  }

  return {
    data: {
      t: timeArray,
      bcg: data
    },
    fs
  };
}

/**
 * 밴드패스 필터 (간단한 구현)
 */
export function bandpass(signal: number[], _fs: number, _low: number, _high: number): number[] {
  // 간단한 밴드패스 필터 구현 (실제로는 더 정교한 필터가 필요)
  const mean = signal.reduce((sum, val) => sum + val, 0) / signal.length;
  return signal.map(val => val - mean);
}

/**
 * 로우패스 필터 (간단한 구현)
 */
export function lowpass(signal: number[], _fs: number, _high: number = 0.6): number[] {
  // 간단한 로우패스 필터 구현
  const mean = signal.reduce((sum, val) => sum + val, 0) / signal.length;
  return signal.map(val => val - mean);
}

/**
 * 피크 검출 (간단한 구현)
 */
export function findPeaks(signal: number[], fs: number): number[] {
  const peaks: number[] = [];
  const threshold = calculateThreshold(signal);
  const minDistance = Math.floor(fs * 0.3); // 최소 0.3초 간격

  for (let i = 1; i < signal.length - 1; i++) {
    if (signal[i] > signal[i - 1] &&
      signal[i] > signal[i + 1] &&
      signal[i] > threshold &&
      (peaks.length === 0 || i - peaks[peaks.length - 1] >= minDistance)) {
      peaks.push(i);
    }
  }

  return peaks;
}

/**
 * 임계값 계산 (MAD 기반)
 */
function calculateThreshold(signal: number[]): number {
  const median = calculateMedian(signal);
  const deviations = signal.map(val => Math.abs(val - median));
  const mad = calculateMedian(deviations);
  return 1.5 * mad;
}

/**
 * 중앙값 계산
 */
function calculateMedian(arr: number[]): number {
  const sorted = [...arr].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 === 0
    ? (sorted[mid - 1] + sorted[mid]) / 2
    : sorted[mid];
}

/**
 * 윈도우 분석
 */
export function windowize(
  sigData: BCGData,
  fs: number,
  winSec: number = 60,
  strideSec: number = 15
): { windows: WindowData[]; peaks: number[] } {
  const signal = sigData.bcg;
  const sigBp = bandpass(signal, fs, 1.0, 10.0);
  const sigLow = lowpass(signal, fs, 0.6);
  const peaks = findPeaks(sigBp, fs);

  const size = Math.floor(winSec * fs);
  const step = Math.floor(strideSec * fs);
  const windows: WindowData[] = [];

  for (let s = 0; s < signal.length - size + 1; s += step) {
    const e = s + size;
    const t0 = sigData.t[s];
    const t1 = sigData.t[e - 1];

    const wPeaks = peaks.filter(p => p >= s && p < e);
    const rr = calculateRRIntervals(wPeaks, fs);
    const validRR = rr.filter(r => r >= 300 && r <= 2000);

    const hr = validRR.length > 0 ? 60000 / calculateMedian(validRR) : NaN;
    const rmssd = calculateRMSSD(validRR);

    // 호흡 추정 (간단한 구현)
    const resp = estimateRespiration(sigLow.slice(s, e), fs);
    const respQ = calculateRespirationQuality(sigLow.slice(s, e), fs);

    // SNR 계산 (간단한 구현)
    const snr = calculateSNR(sigBp.slice(s, e), fs);

    const rrPlausibility = validRR.length >= 3 ? 1.0 : 0.0;
    const dqi = Math.max(0, Math.min(1, Math.tanh((isFinite(snr) ? snr : 0) / 3.0) * rrPlausibility));

    windows.push({
      t_start: t0,
      t_end: t1,
      HR_bpm: hr,
      RMSSD_ms: rmssd,
      Resp_bpm: resp,
      RespQ: respQ,
      SNR: snr,
      DQI: dqi,
      rr_count: validRR.length
    });
  }

  return { windows, peaks };
}

/**
 * RR 간격 계산
 */
function calculateRRIntervals(peaks: number[], fs: number): number[] {
  const rr: number[] = [];
  for (let i = 1; i < peaks.length; i++) {
    rr.push((peaks[i] - peaks[i - 1]) / fs * 1000); // ms 단위
  }
  return rr;
}

/**
 * RMSSD 계산
 */
function calculateRMSSD(rr: number[]): number {
  if (rr.length < 3) return NaN;

  const diffs = [];
  for (let i = 1; i < rr.length; i++) {
    diffs.push(rr[i] - rr[i - 1]);
  }

  const sumSquares = diffs.reduce((sum, diff) => sum + diff * diff, 0);
  return Math.sqrt(sumSquares / diffs.length);
}

/**
 * 호흡 추정 (간단한 구현)
 */
function estimateRespiration(_signal: number[], _fs: number): number {
  // 실제로는 FFT를 사용해야 하지만, 간단한 구현으로 대체
  // 0.1-0.5 Hz 대역에서 주요 주파수 찾기
  return 15.0; // 기본값
}

/**
 * 호흡 품질 계산
 */
function calculateRespirationQuality(_signal: number[], _fs: number): number {
  // 간단한 품질 지표
  return 1.0; // 기본값
}

/**
 * SNR 계산
 */
function calculateSNR(signal: number[], _fs: number): number {
  // 신호 대 잡음비 계산 (간단한 구현)
  const mean = signal.reduce((sum, val) => sum + val, 0) / signal.length;
  const variance = signal.reduce((sum, val) => sum + (val - mean) ** 2, 0) / signal.length;
  return Math.log10(variance + 1e-9);
}
